import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { MagneticButton } from "@/components/magnetic-button";
import { RelatedGuides } from "@/components/related-guides";
import { SiteFooter } from "@/components/site-footer";

export const FAQS = [
  {
    q: "Why is there a white bar at the top of my website on iPhone?",
    a: "Because iOS 26 Safari paints its own tint layer over the status bar / safe area, and it picks the color from your page. If it finds a fixed or sticky element near the top edge, it samples that element's background color. If that element is transparent, Safari gives up and paints the bar white. Your CSS is fine. Your header is the suspect.",
  },
  {
    q: "How do I make the iOS Safari safe area match my page background?",
    a: "Three things together: set viewport-fit=cover on the viewport meta tag, give html and body an explicit background-color (not transparent), and update both the theme-color meta tag and those backgrounds from JavaScript on scroll. Safari samples that combination for the safe area tint, so the status bar follows whatever content is currently near the top.",
  },
  {
    q: "Does the theme-color meta tag still work in iOS 26 Safari?",
    a: "Not on its own. Safari 26 stopped honoring a static theme-color meta tag for its chrome. What still works in practice is updating the meta tag AND the html and body background colors together from JavaScript. Safari picks up those changes on scroll, which is how you get the tint to follow your content.",
  },
  {
    q: "Why doesn't viewport-fit=cover fix the white bar?",
    a: "viewport-fit=cover does its job: it extends your layout viewport under the notch and makes env(safe-area-inset-top) return real values. But the tint layer Safari paints on top of your page is a separate thing. You need viewport-fit=cover as a prerequisite, then you still have to win the tinting game.",
  },
  {
    q: "Can I have a sticky header without the white bar on iOS?",
    a: "Not with position: sticky or position: fixed, no. Safari samples those elements for the status bar tint no matter what top offset you give them. We tested offsetting a sticky header below the safe area and the white bar survived. The workarounds are an absolutely positioned nav that scrolls away, or a nav that follows the viewport with a transform, which Safari's sampler ignores.",
  },
  {
    q: "Does apple-mobile-web-app-status-bar-style black-translucent help?",
    a: "Only if your site runs as a home screen web app. In the regular Safari browser that meta tag is ignored completely, so it will not remove the white bar for normal visitors.",
  },
  {
    q: "How do I test Safari status bar tinting properly?",
    a: "On a real iPhone running iOS 26, with Allow Website Tinting enabled in Safari settings. The Simulator misses several of these behaviors, and desktop Safari misses all of them. Also test at scroll position zero and mid-scroll, because Safari treats those states differently.",
  },
];

export const Route = createFileRoute("/guides/ios-safari-white-bar-fix")({
  head: () => ({
    meta: [
      { title: "iOS 26 Safari White Bar & Safe Area Fix · theme-color, viewport-fit, Liquid Glass | the Vibing Company®" },
      {
        name: "description",
        content:
          "Fix the white bar above the notch in iOS 26 Safari: why the safe area tints white, why theme-color and viewport-fit=cover alone aren't enough, why your sticky header is the culprit, and the working edge-to-edge recipe.",
      },
      { property: "og:title", content: "iOS 26 Safari White Bar & Safe Area Fix · theme-color, viewport-fit, Liquid Glass" },
      ...ogImageMeta("guides-ios-safari-white-bar", "Share card: the iOS 26 Safari white bar fix"),
      {
        property: "og:description",
        content:
          "The white bar over the notch is Safari's safe area tint, and your sticky header feeds it. Everything we tried, what failed, and the recipe that works.",
      },
      { property: "og:url", content: "https://vibing.inc/guides/ios-safari-white-bar-fix" },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "iOS 26 Safari White Bar & Safe Area Fix" },
      { name: "twitter:description", content: "Why the iOS 26 Safari safe area tints white, and the recipe that actually fixes it." },
      { name: "keywords", content: "ios 26 safari white bar, safe area safari ios, safari status bar color, viewport-fit cover, theme-color ios 26, safe-area-inset-top, ios safari notch, liquid glass safari, edge to edge website ios" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/guides/ios-safari-white-bar-fix" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "iOS 26 Safari White Bar & Safe Area Fix: theme-color, viewport-fit, and Liquid Glass",
          description:
            "Why iOS 26 Safari tints the safe area white, the fixed/sticky element caveat, and the working recipe for edge-to-edge websites.",
          author: { "@type": "Organization", name: "the Vibing Company" },
          publisher: { "@type": "Organization", name: "the Vibing Company" },
          datePublished: "2026-06-12",
          mainEntityOfPage: "https://vibing.inc/guides/ios-safari-white-bar-fix",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Guide,
});

function Guide() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
      <Hero />
      <Body />
      <FAQ />
      <CTA />
      <RelatedGuides current="/guides/ios-safari-white-bar-fix" service={{ to: "/services/vibe-coding-rescue", label: "Vibe coding rescue" }} />
      </main>
      <SiteFooter />
    </div>
  );
}


function Hero() {
  return (
    <section aria-labelledby="g-h" className="relative mx-auto max-w-[1100px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-12">
      <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
        Battle report · iOS 26 Safari · June 2026
      </p>
      <h1 id="g-h" className="mt-8 font-display tracking-[-0.035em] leading-[1.02]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
        The white bar that would not die.
      </h1>
      <p className="mt-10 max-w-[62ch] text-xl md:text-2xl leading-snug text-foreground">
        You shipped a beautiful site. You open it on an iPhone and there it is: a glaring white strip over the notch, tinting the safe area in iOS 26 Safari and blocking your edge-to-edge dreams. <code className="font-mono text-[0.85em]">viewport-fit=cover</code> didn't fix it. <code className="font-mono text-[0.85em]">theme-color</code> didn't fix it. <code className="font-mono text-[0.85em]">safe-area-inset-top</code> padding didn't fix it. We spent a night fighting it. This is everything we tried, everything that failed, and the recipe that finally worked.
      </p>
    </section>
  );
}

function Section({ h, children }: { h: string; children: ReactNode }) {
  return (
    <section className="grid md:grid-cols-12 gap-8 md:gap-10 border-t border-border/60 pt-12 md:pt-16">
      <div className="md:col-span-4">
        <h2 className="mt-4 font-display tracking-[-0.03em] leading-[1.02]" style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}>
          {h}
        </h2>
      </div>
      <div className="md:col-span-8 space-y-5 text-base md:text-lg leading-relaxed text-foreground/90 max-w-[68ch]">
        {children}
      </div>
    </section>
  );
}

function Code({ children }: { children: string }) {
  return (
    <pre tabIndex={0} className="rounded-2xl bg-foreground text-background p-5 md:p-6 overflow-x-auto font-mono text-[13px] leading-relaxed">
      <code>{children}</code>
    </pre>
  );
}

function Body() {
  return (
    <article className="mx-auto max-w-[1100px] px-6 md:px-10 py-12 md:py-20 space-y-16 md:space-y-24">
      <Section h="The crime scene">
        <p>
          The symptom is always the same. You added <code className="font-mono text-[0.9em]">viewport-fit=cover</code>. You set your background colors. You read three blog posts and a WebKit bug thread. And iOS Safari still draws an opaque bar across the top of the screen, right where the notch lives, in a shade of white you never picked.
        </p>
        <p>
          Here is the part nobody tells you up front: that bar contains zero of your pixels. No amount of <code className="font-mono text-[0.9em]">overflow</code>, <code className="font-mono text-[0.9em]">clip</code>, z-index or prayer will remove it, because it is Safari's own chrome, painted on top of your page. You cannot style it. You can only influence what color Safari picks for it, and whether Safari decides to show your content through it.
        </p>
        <p>
          With iOS 26 and the Liquid Glass redesign, Apple rewrote the rules for how that color gets picked. They also forgot to write the rules down anywhere. The community reverse engineered most of it, and we verified the rest the hard way, on a real device, at 4am.
        </p>
      </Section>

      <Section h="What Apple changed in iOS 26">
        <p>
          For years the answer was a meta tag. You set <code className="font-mono text-[0.9em]">theme-color</code>, Safari painted its chrome in that color, everyone went home early. Safari 26 ended that arrangement. The tag still parses. Safari simply stopped caring about its static value.
        </p>
        <p>
          Instead, Safari now derives the tint from your page itself, in this order: first it samples the <code className="font-mono text-[0.9em]">background-color</code> of any <code className="font-mono text-[0.9em]">position: fixed</code> or <code className="font-mono text-[0.9em]">position: sticky</code> element near the top edge of the viewport. If it finds none, it falls back to the background color of your <code className="font-mono text-[0.9em]">html</code> and <code className="font-mono text-[0.9em]">body</code> elements. If those are transparent, you get white. White like the bar you are staring at.
        </p>
        <p>
          The sampler has opinions. It ignores absolutely positioned children. It ignores pseudo elements. It ignores anything that is in normal flow, even if it visually touches the top of the screen. It does read elements with <code className="font-mono text-[0.9em]">opacity: 0</code> and <code className="font-mono text-[0.9em]">pointer-events: none</code>, so your hidden modal backdrop is busy tinting Safari's toolbar right now. Only <code className="font-mono text-[0.9em]">display: none</code> takes an element out of the game.
        </p>
      </Section>

      <Section h="The graveyard of fixes that don't fix it">
        <p>Things we tried, in roughly increasing order of desperation:</p>
        <p>
          <strong>viewport-fit=cover alone.</strong> Necessary, insufficient. It extends your layout under the notch and unlocks <code className="font-mono text-[0.9em]">env(safe-area-inset-top)</code>, which is great, but the tint layer sits on top of your extended layout and keeps being white.
        </p>
        <p>
          <strong>Overflow and clipping tricks.</strong> The bar is browser chrome. Clipping your own content harder does nothing except clip your own content.
        </p>
        <p>
          <strong>apple-mobile-web-app-status-bar-style: black-translucent.</strong> Applies to home screen web apps only. Safari the browser reads it, nods politely, and ignores it.
        </p>
        <p>
          <strong>A static theme-color meta tag.</strong> See above. Safari 26 treats it as decorative.
        </p>
        <p>
          <strong>Offsetting the sticky header below the safe area.</strong> This one hurt. We moved the safe area offset from the header's padding into its <code className="font-mono text-[0.9em]">top</code> and <code className="font-mono text-[0.9em]">margin-top</code>, so the sticky box never touched the screen edge. The community write-ups say the sampler only looks within a few pixels of the top. Our device said otherwise. The white bar survived. Sticky elements get sampled no matter where they stick.
        </p>
      </Section>

      <Section h="The experiment that cracked it">
        <p>
          The breakthrough came from a tiny A/B test on our own page. We wired up a script that fades the <code className="font-mono text-[0.9em]">html</code> and <code className="font-mono text-[0.9em]">body</code> backgrounds (plus the meta tag) toward our footer color as you approach the bottom of the page. Then we scrolled to the footer and looked at both ends of the screen.
        </p>
        <p>
          Bottom of the screen: perfectly tinted in footer gray. Top of the screen: still glaring white, even though the fallback colors were now dark. Same page, same colors, two different results. The only difference between those two edges was that one of them had a sticky element parked on it. Our transparent sticky nav was feeding the sampler, the sampler was finding nothing it could use, and Safari was resolving that to opaque white.
        </p>
        <p>
          We switched the nav to <code className="font-mono text-[0.9em]">position: absolute</code> and reloaded. Clean notch, content flowing underneath, tint following the page. Case closed. The navbar did it.
        </p>
      </Section>

      <Section h="The recipe that works">
        <p><strong>Step 1: viewport-fit=cover.</strong> Still the entry ticket.</p>
        <Code>{`<meta name="viewport"
  content="width=device-width, initial-scale=1, viewport-fit=cover" />`}</Code>
        <p>
          <strong>Step 2: split your backgrounds.</strong> Give <code className="font-mono text-[0.9em]">html</code> and <code className="font-mono text-[0.9em]">body</code> an explicit color and treat them as the tint layer Safari reads. Paint the actual page on your app container, so the tint layer can change color without your content noticing.
        </p>
        <Code>{`html, body {
  /* the tint layer: Safari samples this as its fallback */
  background-color: #fafbff;
}
#app {
  /* the page itself paints here */
  min-height: 100dvh;
  background-color: #fafbff;
}`}</Code>
        <p>
          <strong>Step 3: drive the tint from JavaScript.</strong> Update the meta tag and both root backgrounds together on scroll. Safari picks the changes up, which means the safe areas can match whatever content is currently near them. We fade to the footer color over the last viewport of scroll:
        </p>
        <Code>{`const TOP = "#fafbff";    // page background
const BOTTOM = "#1e2023"; // footer color

function applyTint(color) {
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", color);
  document.documentElement.style.backgroundColor = color;
  document.body.style.backgroundColor = color;
}

function mix(a, b, t) {
  const ch = (h, i) => parseInt(h.slice(i, i + 2), 16);
  const hex = (n) => Math.round(n).toString(16).padStart(2, "0");
  return "#" + hex(ch(a, 1) + (ch(b, 1) - ch(a, 1)) * t)
             + hex(ch(a, 3) + (ch(b, 3) - ch(a, 3)) * t)
             + hex(ch(a, 5) + (ch(b, 5) - ch(a, 5)) * t);
}

let raf = null;
window.addEventListener("scroll", () => {
  if (raf) return;
  raf = requestAnimationFrame(() => {
    raf = null;
    const remaining = document.documentElement.scrollHeight
      - window.innerHeight - window.scrollY;
    const t = Math.min(1, Math.max(0, 1 - remaining / window.innerHeight));
    applyTint(mix(TOP, BOTTOM, t));
  });
}, { passive: true });`}</Code>
        <p>
          <strong>Step 4: keep fixed and sticky elements away from the top edge.</strong> This is the step everyone skips and the reason the other steps seem broken. Any <code className="font-mono text-[0.9em]">position: fixed</code> or <code className="font-mono text-[0.9em]">position: sticky</code> element will hijack the top tint, and a transparent one hijacks it to white. An absolutely positioned nav that scrolls away with the page keeps the notch clean.
        </p>
        <Code>{`/* clean notch */
header { position: absolute; top: env(safe-area-inset-top); }

/* white bar generator */
header { position: sticky; top: 0; background: transparent; }`}</Code>
        <p>
          <strong>Step 5: hide hidden overlays properly.</strong> Modal backdrops waiting in the DOM at <code className="font-mono text-[0.9em]">opacity: 0</code> still feed the sampler. Use <code className="font-mono text-[0.9em]">display: none</code> until they actually open.
        </p>
      </Section>

      <Section h="But I want my sticky navbar">
        <p>
          We hear you. We wanted ours too. The honest answer is that on iOS 26 you are choosing between a permanently pinned <code className="font-mono text-[0.9em]">position: sticky</code> nav and a clean notch. You do not get both, because the sampler keys on the position value itself, even when the element is nowhere near the edge it tints.
        </p>
        <p>
          If the nav matters more, keep it sticky and accept the bar. You can at least make the bar less offensive by giving the sticky element a real background color that matches your design instead of leaving it transparent, since transparent is what resolves to white.
        </p>
        <p>
          If the notch matters more, there are two patterns that keep navigation reachable without triggering the sampler. One: a reveal-on-scroll-up nav, absolutely positioned, translated to the viewport with a transform while the user scrolls upward. Safari ignores transforms. Two: the simple version, a nav that lives at the top of the page and scrolls away, the way half the design-forward web already works. Your visitors will survive.
        </p>
      </Section>

      <Section h="Testing without losing your mind">
        <p>
          Test on a real iPhone running iOS 26. The Simulator reproduces some of the tinting behavior and quietly skips the rest, which is a fantastic way to ship a fix that does not work.
        </p>
        <p>
          Know the two states. At scroll position zero, Safari paints the fallback color and will not composite your actual content behind the status bar. Once the page has any scroll at all, it can. If your first screen is a full-bleed image and the top still shows a flat color, that is this, and the workaround is a small scroll runway you jump past on load.
        </p>
        <p>
          And remember the user setting. Safari has an Allow Website Tinting toggle, and when it is off, the chrome reverts to system colors no matter how clever your setup is. Design something that still looks intentional in plain white and plain black, because some fraction of your audience lives there.
        </p>
      </Section>
    </article>
  );
}

function FAQ() {
  return (
    <section aria-labelledby="faq-h" className="relative border-t border-border/60 mx-auto max-w-[1100px] px-6 md:px-10 py-20 md:py-32">
      <h2 id="faq-h" className="fluid-h2 font-display max-w-[20ch]">The white bar, answered.</h2>
      <dl className="mt-14 border-t border-border">
        {FAQS.map((f) => (
          <div key={f.q} className="grid md:grid-cols-12 gap-6 py-8 border-b border-border">
            <dt className="md:col-span-5 font-display text-xl md:text-2xl tracking-[-0.02em] leading-snug">{f.q}</dt>
            <dd className="md:col-span-6 md:col-start-7 text-base md:text-lg text-muted-foreground leading-relaxed">{f.a}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function CTA() {
  return (
    <section aria-labelledby="cta-h" className="relative border-t border-border/60 bg-foreground text-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <h2 id="cta-h" className="font-display tracking-[-0.035em] leading-[1.02]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
            Stuck on a bug like this?
          </h2>
          <p className="mt-8 text-lg md:text-xl text-background/70 max-w-[52ch] leading-snug">
            We debug the weird stuff for a living. Bring us the bar, the glitch, or the half-finished app, and get it back working in a week.
          </p>
        </div>
        <div className="md:col-span-4 md:col-start-9 space-y-4">
          <Link to="/services/vibe-coding-rescue" className="block font-display text-electric-bright" style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.5rem)" }}>
            → Rescue a stuck project
          </Link>
          <a href="https://cal.com/vibinc/30min" target="_blank" rel="noopener noreferrer" className="block font-display text-electric-bright" style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.5rem)" }}>
            → Talk to Jon
          </a>
        </div>
      </div>
    </section>
  );
}
